import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updateGeneralSettings } from 'services/transparency-config-service';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

export const GeneralSettings = ({ config, onUpdate, companyKey }) => {
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      transparencyEnabled: config.transparencyEnabled || false,
      publicAccessEnabled: config.publicAccessEnabled || false,
      protectedAccessEnabled: config.protectedAccessEnabled || false,
      showTrendPublicly: config.showTrendPublicly || false,
    },
  });

  const transparencyEnabled = watch('transparencyEnabled');

  const mutation = useMutation(
    (data) => updateGeneralSettings(companyKey, data),
    {
      onSuccess: () => {
        MySwal.fire({
          title: 'Sucesso!',
          text: 'Configurações salvas com sucesso',
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: 'Erro!',
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    mutation.mutate(data);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          Configurações Gerais
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Controle se o Centro de Transparência está ativo e quais modos de acesso estão habilitados.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Master Toggle */}
        <Box sx={{ mb: 3 }}>
          <Controller
            control={control}
            name="transparencyEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={500}>
                      Habilitar Centro de Transparência
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ativa ou desativa completamente o Centro de Transparência
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        {!transparencyEnabled && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            O Centro de Transparência está desabilitado. Nenhum acesso público ou protegido será permitido.
          </Alert>
        )}

        {/* Access Mode Toggles */}
        <Box sx={{ ml: 4, mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="publicAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>Habilitar Acesso Público</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Permite visualização pública sem autenticação
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Box sx={{ ml: 4, mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="protectedAccessEnabled"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>Habilitar Acesso Protegido</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Permite acesso via token de tempo limitado
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Trend Toggle */}
        <Box sx={{ mb: 3, opacity: transparencyEnabled ? 1 : 0.5 }}>
          <Controller
            control={control}
            name="showTrendPublicly"
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                disabled={!transparencyEnabled}
                control={
                  <Checkbox
                    checked={value}
                    onChange={onChange}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography>Mostrar Indicador de Tendência Publicamente</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Exibe o indicador textual de tendência na página pública
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : 'Salvar'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
